import { useState } from "react";
import {
  useMyPackaging,
  type CustomerPackaging,
} from "@/api/use-packaging-api";
import { Package, ChevronDown, Check, PenLine, Star } from "lucide-react";
import { Button, Popover, PopoverContent, PopoverTrigger } from "@heroui/react";
import { useTranslation } from "react-i18next";

export interface PackagingConfig {
  id: string;
  name: string;
  length: number;
  width: number;
  height: number;
  dimensionUnit: "cm" | "in";
  isDefault?: boolean;
}

interface PackagingSelectorProps {
  onSelect: (config: PackagingConfig | null) => void;
  selectedId?: string | null;
}

export function PackagingSelector({
  onSelect,
  selectedId,
}: PackagingSelectorProps) {
  const { t } = useTranslation("app");
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useMyPackaging();
  const packaging = data?.boxes ?? [];

  const selectedPackaging = selectedId
    ? packaging.find((p: CustomerPackaging) => p.id === selectedId)
    : null;

  const formatDimensions = (pkg: CustomerPackaging) => {
    return `${pkg.length} × ${pkg.width} × ${pkg.height} ${pkg.dimensionUnit}`;
  };

  return (
    <Popover isOpen={open} onOpenChange={setOpen} placement="bottom-end">
      <PopoverTrigger>
        <Button
          type="button"
          variant="bordered"
          className="h-auto py-2 px-3 gap-2 justify-start"
          endContent={<ChevronDown className="h-3.5 w-3.5 opacity-50" />}
          startContent={
            selectedPackaging ? (
              <Package className="h-4 w-4 shrink-0" />
            ) : (
              <PenLine className="h-4 w-4 shrink-0" />
            )
          }
        >
          <div className="flex flex-col items-start text-left">
            <span className="font-medium">
              {selectedPackaging
                ? selectedPackaging.name
                : t("packagingSelector.custom")}
            </span>
            <span className="text-tiny text-default-500">
              {selectedPackaging
                ? formatDimensions(selectedPackaging)
                : t("packagingSelector.manualDetails")}
            </span>
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2">
        <div className="w-full space-y-1">
          <button
            type="button"
            onClick={() => {
              onSelect(null);
              setOpen(false);
            }}
            className={`w-full flex items-center gap-3 p-3 rounded-medium text-left hover:bg-default-100 transition-colors ${
              !selectedId ? "bg-default-100" : ""
            }`}
          >
            <PenLine className="h-5 w-5 text-default-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-medium">{t("packagingSelector.custom")}</div>
              <div className="text-tiny text-default-500">
                {t("packagingSelector.manualDetails")}
              </div>
            </div>
            {!selectedId && <Check className="h-4 w-4 text-primary shrink-0" />}
          </button>

          {packaging.length > 0 && (
            <>
              <div className="border-t border-divider my-1" />
              <div className="px-3 py-1.5 text-tiny font-semibold uppercase tracking-wider text-default-500">
                {t("packagingSelector.myPackaging")}
              </div>
            </>
          )}

          {packaging.map((pkg: CustomerPackaging) => {
            const isSelected = selectedId === pkg.id;
            return (
              <button
                key={pkg.id}
                type="button"
                onClick={() => {
                  onSelect({
                    id: pkg.id,
                    name: pkg.name,
                    length: pkg.length,
                    width: pkg.width,
                    height: pkg.height,
                    dimensionUnit: pkg.dimensionUnit,
                    isDefault: pkg.isDefault,
                  });
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-3 p-3 rounded-medium text-left hover:bg-default-100 transition-colors ${
                  isSelected ? "bg-default-100" : ""
                }`}
              >
                <Package className="h-5 w-5 text-default-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate flex items-center gap-1.5">
                    {pkg.name}
                    {pkg.isDefault && (
                      <Star className="h-3 w-3 text-warning fill-warning" />
                    )}
                  </div>
                  <div className="text-tiny text-default-500">
                    {formatDimensions(pkg)}
                  </div>
                </div>
                {isSelected && (
                  <Check className="h-4 w-4 text-primary shrink-0" />
                )}
              </button>
            );
          })}

          {isLoading && (
            <div className="px-3 py-2 text-tiny text-default-500">
              {t("packagingSelector.loading")}
            </div>
          )}

          {!isLoading && packaging.length === 0 && (
            <div className="px-3 py-2 text-tiny text-default-500">
              {t("packagingSelector.empty")}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
